import React, { useMemo, useState } from 'react';
import '@/components/modals/modal-buttons.css';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogClose,
    DialogDescription
} from '@/components/ui/dialog';
import { ProfileInfoType, UserInfoType } from '@/types/profile';
import { useSelector } from 'react-redux';
import { RootState } from '@/store';
import { Mail, Phone, Globe, MapPin, DollarSign, Calendar, CalendarX } from 'lucide-react';
import {
    FacebookIcon,
    InstagramIcon,
    YoutubeIcon,
    SpotifyIcon,
    SoundcloudIconSVG,
    AppleMusicIconSVG,
    BandcampIconSVG,
    TikTokIcon,
    SnapchatIcon,
    DiscordIcon,
    TwitchIcon,
    XLogoIcon,
    LinkedInIcon,
    ThreadsIcon,
    NewgroundsIcon,
    XCloseIcon
} from '@/components/icons';
import { getUserDisplayName } from '@/utils/userDisplay';
import { CollabEntry, endCollaboration } from '@/api/collaborations';
import { format } from 'date-fns';

interface CollabInfoModalProps {
    isOpen: boolean
    onClose: () => void
    collaborator: ProfileInfoType | undefined
    collabHistory?: CollabEntry[]
    onEndCollab?: (collabId: string) => void
}

const CollabInfoModal: React.FC<CollabInfoModalProps> = ({
    isOpen,
    onClose,
    collaborator,
    collabHistory,
    onEndCollab,
}) => {
    const [localHistory, setLocalHistory] = useState<CollabEntry[]>(collabHistory || []);
    const [confirmingEndId, setConfirmingEndId] = useState<string | null>(null);
    const [endingId, setEndingId] = useState<string | null>(null);

    // Sync local history when the prop changes (e.g. parent re-fetches).
    // Preserve optimistic endedByUserId when the server payload omits it (same tick / legacy rows).
    React.useEffect(() => {
        const incoming = collabHistory || [];
        setLocalHistory((prev) =>
            incoming.map((inc) => {
                if (inc.status !== 'ended' || inc.endedByUserId) return inc;
                const p = prev.find((x) => String(x.collabId) === String(inc.collabId));
                if (p?.endedByUserId) {
                    return { ...inc, endedByUserId: p.endedByUserId };
                }
                return inc;
            })
        );
    }, [collabHistory]);

    const viewer = useSelector((state: RootState) => state.auth.user);

    if (!collaborator) {
        return null;
    }

    const projectCreatorDisplayName = (entry: CollabEntry): string => {
        const viewerId = viewer?._id?.toString();
        const collaboratorId = collaborator.userData?._id?.toString();

        // Preferred source: explicit pin creator user id.
        let creatorId = entry.jamboardPinCreatorUserId?.toString() || '';

        // Backward-compatible fallback for older collabs:
        // JamBoard apply flow has initiatedBy = applicant, so creator is the opposite user.
        if (!creatorId && entry.startedFrom === 'jamboard') {
            const initiatorId = entry.initiatedBy?.toString();
            if (initiatorId && viewerId && collaboratorId) {
                if (initiatorId === viewerId) {
                    creatorId = collaboratorId;
                } else if (initiatorId === collaboratorId) {
                    creatorId = viewerId;
                }
            }
        }

        if (viewerId && creatorId === viewerId && viewer) {
            return getUserDisplayName({
                userData: viewer as unknown as UserInfoType,
                username: viewer.username,
                fallback: 'You',
            });
        }
        if (collaboratorId && creatorId === collaboratorId) {
            return getUserDisplayName({ profileInfo: collaborator, fallback: 'Collaborator' });
        }

        // Final fallback: collaborator display name is usually closer to truth in 1:1 collabs
        // than a generic "Unknown" label.
        return getUserDisplayName({ profileInfo: collaborator, fallback: 'Unknown' });
    };

    const endedByDisplayName = (entry: CollabEntry): string | null => {
        const endedById = entry.endedByUserId?.toString();
        const viewerId = viewer?._id?.toString();
        const collaboratorId = collaborator.userData?._id?.toString();

        if (endedById && viewerId && endedById === viewerId && viewer) {
            return getUserDisplayName({
                userData: viewer as unknown as UserInfoType,
                username: viewer.username,
                fallback: 'You',
            });
        }
        if (endedById && collaboratorId && endedById === collaboratorId) {
            return getUserDisplayName({ profileInfo: collaborator, fallback: 'Collaborator' });
        }

        // Legacy ended records might not have endedByUserId stored.
        return null;
    };

    const displayName = getUserDisplayName({
        profileInfo: collaborator,
        fallback: 'Collaborator'
    });

    const showSensitiveContact = (collabHistory ?? []).some(
        (c) => c.status === 'accepted'
    );

    const socialPlatforms = [
        { name: 'Facebook', icon: FacebookIcon, url: collaborator.facebook },
        { name: 'Instagram', icon: InstagramIcon, url: collaborator.instagram },
        { name: 'YouTube', icon: YoutubeIcon, url: collaborator.youtube },
        { name: 'Spotify', icon: SpotifyIcon, url: collaborator.spotify },
        { name: 'SoundCloud', icon: SoundcloudIconSVG, url: collaborator.soundcloud },
        { name: 'Apple Music', icon: AppleMusicIconSVG, url: collaborator.applemusic },
        { name: 'Bandcamp', icon: BandcampIconSVG, url: collaborator.bandcamp },
        { name: 'TikTok', icon: TikTokIcon, url: collaborator.tiktok },
        { name: 'Snapchat', icon: SnapchatIcon, url: collaborator.snapchat },
        { name: 'Discord', icon: DiscordIcon, url: collaborator.discord },
        { name: 'Twitch', icon: TwitchIcon, url: collaborator.twitch },
        { name: 'X', icon: XLogoIcon, url: collaborator.x },
        { name: 'LinkedIn', icon: LinkedInIcon, url: collaborator.linkedin },
        { name: 'Threads', icon: ThreadsIcon, url: collaborator.threads },
        { name: 'Newgrounds', icon: NewgroundsIcon, url: collaborator.newgrounds },
    ].filter((platform) => platform.url);

    const jamBoardHistory = useMemo(() => {
        const filtered = localHistory.filter(
            (c) =>
                (c.status === 'accepted' || c.status === 'ended') &&
                c.startedFrom === 'jamboard' &&
                !!c.jamboardPinTitle
        );
        return [...filtered].sort((a, b) => {
            const aEnd = a.status === 'ended';
            const bEnd = b.status === 'ended';
            if (aEnd !== bEnd) return aEnd ? 1 : -1;
            const dateA = new Date(
                aEnd && a.endedAt ? a.endedAt : (a.acceptedAt || a.createdAt)
            ).getTime();
            const dateB = new Date(
                bEnd && b.endedAt ? b.endedAt : (b.acceptedAt || b.createdAt)
            ).getTime();
            return dateB - dateA;
        });
    }, [localHistory]);

    const handleEndClick = (collabId: string) => {
        setConfirmingEndId(collabId);
    };

    const handleConfirmEnd = async (collabId: string) => {
        setEndingId(collabId);
        try {
            const success = await endCollaboration(collabId);
            if (success) {
                const endedAt = new Date().toISOString();
                const endedByUserId =
                    viewer?._id != null ? String(viewer._id) : null;
                setLocalHistory((prev) =>
                    prev.map((c) =>
                        c.collabId === collabId
                            ? { ...c, status: 'ended', endedAt, endedByUserId }
                            : c
                    )
                );
                onEndCollab?.(collabId);
            }
        } catch (error) {
            console.error('Error ending collaboration:', error);
        } finally {
            setEndingId(null);
            setConfirmingEndId(null);
        }
    };

    const handleCancelEnd = () => {
        setConfirmingEndId(null);
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="flex max-h-[88vh] min-h-0 flex-col overflow-hidden bg-white px-3 py-2 font-sans shadow-lg gap-2 md:px-4 md:py-3 max-w-2xl rounded-lg">
                <DialogClose className="absolute right-4 top-4 p-1.5 border border-aquarius rounded-full cursor-pointer h-7 w-7 flex items-center justify-center">
                    <XCloseIcon width={14} height={14} />
                </DialogClose>
                <DialogHeader className="shrink-0 space-y-1">
                    <DialogTitle className="text-lg md:text-xl font-bold text-gray-900 pr-8">
                        {displayName} is ready to collaborate with you!
                    </DialogTitle>
                    <DialogDescription className="sr-only">
                        View and manage your collaboration with {displayName}
                    </DialogDescription>
                </DialogHeader>
                {collaborator.description && (
                    <div className="mt-2 shrink-0">
                        <h3 className="text-base font-semibold text-gray-700 mb-1">
                            About user
                        </h3>
                        <p className="text-gray-600 text-sm">
                            {collaborator.description}
                        </p>
                    </div>
                )}
                <div className="mt-2 grid shrink-0 grid-cols-1 gap-3 md:grid-cols-2">
                    <div>
                        <h3 className="text-base font-semibold text-gray-700 mb-1">
                            Contact Information
                        </h3>
                        <div className="space-y-1 text-sm">
                            <div className="flex items-center">
                                <span className="mr-1 flex-shrink-0">
                                    <Mail className="w-4 h-4 text-aquarius" />
                                </span>
                                {collaborator.userData?.email && showSensitiveContact ? (
                                    <span>{collaborator.userData?.email}</span>
                                ) : (
                                    <span className="text-gray-400">
                                        Hidden
                                    </span>
                                )}
                            </div>
                            <div className="flex items-center">
                                <span className="mr-1 flex-shrink-0">
                                    <Phone className="w-4 h-4 text-aquarius" />
                                </span>
                                {collaborator.userData?.phone && showSensitiveContact ? (
                                    <span>{collaborator.userData?.phone}</span>
                                ) : (
                                    <span className="text-gray-400">
                                        Hidden
                                    </span>
                                )}
                            </div>
                            {collaborator.personalUrl && (
                                <div className="flex items-center">
                                    <span className="mr-1 flex-shrink-0">
                                        <Globe className="w-4 h-4 text-aquarius" />
                                    </span>
                                    <a
                                        href={collaborator.personalUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-blue-500 hover:underline"
                                    >
                                        {collaborator.personalUrl}
                                    </a>
                                </div>
                            )}
                            {collaborator.location &&
                                collaborator.location.displayText && (
                                <div className="flex items-center">
                                    <span className="mr-1 flex-shrink-0">
                                        <MapPin className="w-4 h-4 text-aquarius" />
                                    </span>
                                    <span>
                                        {collaborator.location.displayText}
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                    {socialPlatforms.length > 0 && (
                        <div>
                            <h3 className="text-base font-semibold text-gray-700 mb-1">
                                Social Platforms
                            </h3>
                            <div className="grid grid-cols-2 gap-1 text-sm">
                                {socialPlatforms.map((platform) => (
                                    <div
                                        key={platform.name}
                                        className="flex items-center text-blue-500 hover:underline"
                                    >
                                        <span className="mr-1 flex-shrink-0">
                                            {React.createElement(platform.icon, {
                                                width: '20',
                                                height: '20'
                                            })}
                                        </span>
                                        <a
                                            href={platform.url || ''}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            {platform.name}
                                        </a>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Collaboration history — only this block scrolls when there are many projects */}
                {jamBoardHistory.length > 0 && (
                    <div className="mt-2 flex min-h-0 flex-1 flex-col">
                        <h3 className="mb-1 flex-shrink-0 text-base font-semibold text-gray-700">
                            Collaboration History
                        </h3>
                        <div className="min-h-0 flex-1 overflow-y-auto overscroll-y-contain pr-1">
                            <div className="space-y-2">
                                {jamBoardHistory.map((entry) => {
                                    const startDate = entry.acceptedAt
                                        ? format(new Date(entry.acceptedAt), 'MMMM d, yyyy')
                                        : entry.createdAt
                                            ? format(new Date(entry.createdAt), 'MMMM d, yyyy')
                                            : null;
                                    const isConfirming = confirmingEndId === entry.collabId;
                                    const isEnding = endingId === entry.collabId;
                                    const isEnded = entry.status === 'ended';
                                    const endedByName = isEnded ? endedByDisplayName(entry) : null;

                                    return (
                                        <div
                                            key={entry.collabId}
                                            className={`border border-gray-200 rounded-lg p-2.5 ${
                                                isEnded
                                                    ? 'opacity-60 bg-gray-50'
                                                    : ''
                                            }`}
                                        >
                                            <div className="grid grid-cols-1 md:grid-cols-[180px_1fr] gap-2 items-start">
                                                <div className="w-full">
                                                    <div className="relative w-full aspect-[16/9] rounded-md overflow-hidden bg-neutral-100 border border-gray-200">
                                                        {entry.jamboardProjectImage ? (
                                                            <img
                                                                src={entry.jamboardProjectImage}
                                                                alt=""
                                                                className="absolute inset-0 w-full h-full object-cover"
                                                                style={{
                                                                    objectPosition: `${entry.jamboardProjectImagePositionX ?? 50}% ${entry.jamboardProjectImagePositionY ?? 50}%`,
                                                                }}
                                                            />
                                                        ) : (
                                                            <div className="absolute inset-0 flex items-center justify-center text-gray-400 text-sm">
                                                            No project image
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                                <div>
                                                    <p className="text-gray-900 font-semibold">
                                                        {entry.jamboardPinTitle}
                                                    </p>
                                                    {entry.jamboardPinDescription && (
                                                        <p className="text-gray-600 mt-0.5 text-xs whitespace-pre-wrap">
                                                            {entry.jamboardPinDescription}
                                                        </p>
                                                    )}
                                                    {(entry.jamboardProjectLocation || entry.jamboardProjectRate) && (
                                                        <div className="mt-1.5 space-y-0.5">
                                                            {entry.jamboardProjectLocation && (
                                                                <div className="flex items-center text-gray-700 text-sm">
                                                                    <MapPin className="w-4 h-4 text-aquarius mr-2 flex-shrink-0" />
                                                                    <span>{entry.jamboardProjectLocation}</span>
                                                                </div>
                                                            )}
                                                            {entry.jamboardProjectRate && (
                                                                <div className="flex items-center text-gray-700 text-sm">
                                                                    <DollarSign className="w-4 h-4 text-aquarius mr-2 flex-shrink-0" />
                                                                    <span>{entry.jamboardProjectRate}</span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                    {startDate && (
                                                        <div className="flex items-center text-gray-500 text-xs mt-1.5">
                                                            <Calendar className="w-3 h-3 mr-1 flex-shrink-0" />
                                                            <span>Started {startDate}</span>
                                                        </div>
                                                    )}
                                                    {isEnded && entry.endedAt && (
                                                        <div className="flex items-center text-gray-500 text-xs mt-1.5">
                                                            <CalendarX className="w-3 h-3 mr-1 flex-shrink-0" />
                                                            <span>
                                                            Ended{' '}
                                                                {format(
                                                                    new Date(entry.endedAt),
                                                                    'MMMM d, yyyy'
                                                                )}
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Created by (left) + End actions (right) */}
                                            <div className="mt-1.5 flex flex-wrap items-center justify-between gap-2">
                                                <p className="text-xs text-gray-500">
                                                Created by:{' '}
                                                    <span className="font-medium text-gray-700">
                                                        {projectCreatorDisplayName(entry)}
                                                    </span>
                                                </p>
                                                <div className="flex flex-shrink-0 items-center gap-2">
                                                    {isEnded && endedByName ? (
                                                        <span className="text-xs text-gray-500">
                                                        Ended by:{' '}
                                                            <span className="font-medium text-gray-700">
                                                                {endedByName}
                                                            </span>
                                                        </span>
                                                    ) : isEnded ? null : isConfirming ? (
                                                        <>
                                                            <span className="text-sm text-gray-600">End this collaboration?</span>
                                                            <button
                                                                onClick={() => handleConfirmEnd(entry.collabId)}
                                                                disabled={isEnding}
                                                                className="px-3 py-1 text-xs bg-red-500 hover:bg-red-600 text-white rounded disabled:opacity-50"
                                                            >
                                                                {isEnding ? 'Ending...' : 'Yes, end it'}
                                                            </button>
                                                            <button
                                                                onClick={handleCancelEnd}
                                                                disabled={isEnding}
                                                                className="px-3 py-1 text-xs border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50"
                                                            >
                                                            Cancel
                                                            </button>
                                                        </>
                                                    ) : (
                                                        <button
                                                            onClick={() => handleEndClick(entry.collabId)}
                                                            className="px-3 py-1 text-xs border border-red-300 text-red-500 hover:bg-red-50 rounded"
                                                        >
                                                        End
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                )}
                <p className={`text-xs font-medium text-amber-800 shrink-0 ${jamBoardHistory.length > 0 ? 'mt-2' : 'mt-3'}`}>
                    Warning: For payments be sure to use buyer/seller protection (e.g. PayPal Goods & Services).
                </p>

            </DialogContent>
        </Dialog>
    );
};

export default CollabInfoModal;
